import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../services/api';
import { PasskeyManager } from './PasskeyManager';

interface AuthModalProps {
  onClose?: () => void;
}

export function AuthModal({ onClose }: AuthModalProps) {
  const {
    isLoading,
    error,
    showLogin,
    showRegister,
    setShowLogin,
    setShowRegister,
    setError,
    register,
    login,
    loginWithRecoveryCode,
    isWebAuthnSupported,
  } = useAuth();

  const [mode, setMode] = useState<'login' | 'password' | 'register' | 'recovery'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [webAuthnAvailable, setWebAuthnAvailable] = useState(true);
  const [platformAvailable, setPlatformAvailable] = useState(false);

  useEffect(() => {
    // Check WebAuthn support synchronously (safe)
    try {
      setWebAuthnAvailable(isWebAuthnSupported());
    } catch {
      setWebAuthnAvailable(false);
    }

    // DISABLED: Safari throws "The operation is insecure" for platform auth check
    // This check is purely cosmetic anyway - just show the login form
    // The actual WebAuthn operation will work when user clicks the button
    setPlatformAvailable(false);
  }, [isWebAuthnSupported]);

  useEffect(() => {
    if (showLogin) setMode('login');
    if (showRegister) setMode('register');
  }, [showLogin, showRegister]);

  const handleLogin = async () => {
    try {
      await login(username || undefined);
      onClose?.();
    } catch {
      // Error handled by hook
    }
  };

  const handleRegister = async () => {
    if (!username.trim()) {
      setError('Username is required');
      return;
    }
    try {
      // Auto-detect device name
      const autoDeviceName = deviceName || detectDeviceName();
      await register(username, displayName || username, autoDeviceName);
      onClose?.();
    } catch {
      // Error handled by hook
    }
  };

  const handleRecovery = async () => {
    if (!username.trim() || !recoveryCode.trim()) {
      setError('Username and recovery code are required');
      return;
    }
    try {
      await loginWithRecoveryCode(username, recoveryCode);
      onClose?.();
    } catch {
      // Error handled by hook
    }
  };

  const detectDeviceName = () => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Mac/.test(ua)) return 'Mac';
    if (/Android/.test(ua)) return 'Android';
    if (/Windows/.test(ua)) return 'Windows PC';
    if (/Linux/.test(ua)) return 'Linux';
    return 'Unknown Device';
  };

  if (!showLogin && !showRegister) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#F8F6F1] rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl border border-border">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-text-primary">
            {mode === 'login' && 'Sign In with Passkey'}
            {mode === 'password' && 'Sign In with Password'}
            {mode === 'register' && 'Create Your Passkey'}
            {mode === 'recovery' && 'Recovery Code Login'}
          </h2>
          <button
            onClick={() => {
              setShowLogin(false);
              setShowRegister(false);
              onClose?.();
            }}
            className="text-text-muted hover:text-text-primary"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* WebAuthn not supported warning */}
        {!webAuthnAvailable && (
          <div className="mb-4 p-3 bg-warning/20 rounded-lg text-warning text-sm">
            Your browser doesn't support passkeys. Please use a modern browser like Chrome, Safari, or Edge.
          </div>
        )}

        {/* Platform authenticator info */}
        {webAuthnAvailable && platformAvailable && mode === 'login' && (
          <div className="mb-4 p-3 bg-profit/20 rounded-lg text-profit text-sm flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Face ID / Touch ID / Windows Hello available
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-loss/20 rounded-lg text-loss text-sm">
            {error}
          </div>
        )}

        {/* Mode tabs */}
        <div className="flex gap-1 mb-6">
          <button
            onClick={() => setMode('login')}
            className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-colors ${
              mode === 'login'
                ? 'bg-primary text-white'
                : 'bg-background text-text-soft hover:bg-[#F8F6F1]'
            }`}
          >
            Passkey
          </button>
          <button
            onClick={() => setMode('password')}
            className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-colors ${
              mode === 'password'
                ? 'bg-primary text-white'
                : 'bg-background text-text-soft hover:bg-[#F8F6F1]'
            }`}
          >
            Password
          </button>
          <button
            onClick={() => setMode('register')}
            className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-colors ${
              mode === 'register'
                ? 'bg-primary text-white'
                : 'bg-background text-text-soft hover:bg-[#F8F6F1]'
            }`}
          >
            Register
          </button>
          <button
            onClick={() => setMode('recovery')}
            className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-colors ${
              mode === 'recovery'
                ? 'bg-primary text-white'
                : 'bg-background text-text-soft hover:bg-[#F8F6F1]'
            }`}
          >
            Recovery
          </button>
        </div>

        {/* Login form */}
        {mode === 'login' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-text-soft mb-1">
                Username (optional)
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Leave empty for passkey login"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-neutral"
              />
              <p className="text-xs text-text-muted mt-1">
                Leave empty to use any registered passkey
              </p>
            </div>

            <button
              onClick={handleLogin}
              disabled={isLoading || !webAuthnAvailable}
              className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                  </svg>
                  Sign in with Passkey
                </>
              )}
            </button>
          </div>
        )}

        {/* Password login form */}
        {mode === 'password' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-text-soft mb-1">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-neutral"
                autoComplete="username"
              />
            </div>

            <div>
              <label className="block text-sm text-text-soft mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-neutral"
                autoComplete="current-password"
              />
            </div>

            <button
              onClick={async () => {
                if (!username.trim() || !password.trim()) {
                  setError('Username and password are required');
                  return;
                }
                try {
                  const result = await api.loginWithPassword(username, password);
                  // Use the same flow as passkey login
                  useAuthStore.getState().setAuth({
                    user: result.user,
                    accessToken: result.tokens.access_token,
                    refreshToken: result.tokens.refresh_token,
                    authType: 'password',
                  });
                  api.setAuthToken(result.tokens.access_token);
                  onClose?.();
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Login failed');
                }
              }}
              disabled={isLoading || !username.trim() || !password.trim()}
              className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  Sign in with Password
                </>
              )}
            </button>

            <p className="text-xs text-text-muted text-center">
              After login, you can add a passkey for faster biometric login.
            </p>
          </div>
        )}

        {/* Register form */}
        {mode === 'register' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-text-soft mb-1">
                Username *
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-neutral"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-text-soft mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Optional display name"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-neutral"
              />
            </div>

            <div>
              <label className="block text-sm text-text-soft mb-1">
                Device Name
              </label>
              <input
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder={`Auto: ${detectDeviceName()}`}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-neutral"
              />
            </div>

            <button
              onClick={handleRegister}
              disabled={isLoading || !webAuthnAvailable || !username.trim()}
              className="w-full py-3 bg-profit text-white rounded-lg font-medium hover:bg-profit/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Passkey
                </>
              )}
            </button>

            <p className="text-xs text-text-muted text-center">
              You'll be prompted to use Face ID, Touch ID, or Windows Hello to create your passkey.
            </p>
          </div>
        )}

        {/* Recovery form */}
        {mode === 'recovery' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-text-soft mb-1">
                Username *
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-neutral"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-text-soft mb-1">
                Recovery Code *
              </label>
              <input
                type="text"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-neutral font-mono"
                required
              />
            </div>

            <button
              onClick={handleRecovery}
              disabled={isLoading || !username.trim() || !recoveryCode.trim()}
              className="w-full py-3 bg-warning text-white rounded-lg font-medium hover:bg-warning/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  Login with Recovery Code
                </>
              )}
            </button>

            <p className="text-xs text-text-muted text-center">
              Recovery codes are single-use. After login, consider registering a new passkey.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function AuthButton() {
  const { isAuthenticated, user, authType, logout, setShowLogin } = useAuth();
  const [showPasskeyManager, setShowPasskeyManager] = useState(false);

  if (authType === 'dev_mode') {
    return (
      <button
        onClick={() => setShowLogin(true)}
        className="flex items-center gap-2 px-3 py-1.5 bg-warning/20 text-warning rounded-lg text-sm hover:bg-warning/30"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Dev Mode - Login
      </button>
    );
  }

  if (!isAuthenticated) {
    return (
      <button
        onClick={() => setShowLogin(true)}
        className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary-muted"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
        </svg>
        Sign In
      </button>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-sm">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-medium">
            {user?.display_name?.[0]?.toUpperCase() || 'U'}
          </div>
          <span className="text-text-soft">{user?.display_name || user?.username}</span>
        </div>
        <button
          onClick={() => setShowPasskeyManager(true)}
          className="text-text-muted hover:text-primary transition-colors"
          title="Manage Passkeys"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </button>
        <button
          onClick={logout}
          className="text-text-muted hover:text-loss transition-colors"
          title="Sign out"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </button>
      </div>

      {/* Passkey Manager Modal */}
      {showPasskeyManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#F8F6F1] rounded-xl p-6 w-full max-w-lg mx-4 shadow-2xl border border-border max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-text-primary">Security Settings</h2>
              <button
                onClick={() => setShowPasskeyManager(false)}
                className="text-text-muted hover:text-text-primary"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <PasskeyManager />
          </div>
        </div>
      )}
    </>
  );
}

export function ReauthModal({
  isOpen,
  onSuccess,
  onCancel,
  message = 'Please verify your identity to continue',
}: {
  isOpen: boolean;
  onSuccess: (reauthToken: string) => void;
  onCancel: () => void;
  message?: string;
}) {
  const { reauth, isLoading, error, setError } = useAuth();

  const handleReauth = async () => {
    const token = await reauth();
    if (token) {
      onSuccess(token);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setError(null);
    }
  }, [isOpen, setError]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#F8F6F1] rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl border border-border">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-warning/20 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Verify Identity</h3>
            <p className="text-sm text-text-soft">{message}</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-loss/20 rounded-lg text-loss text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2 px-4 bg-background border border-border rounded-lg text-text-soft hover:bg-[#F8F6F1]"
          >
            Cancel
          </button>
          <button
            onClick={handleReauth}
            disabled={isLoading}
            className="flex-1 py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary-muted disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                </svg>
                Verify
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
